import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { getCurrentPhase, getTrackingMode } from '../../utils/cycleCalculator'
import allPhasesData from '../../data/allPhases.json'
import moonPhasesData from '../../data/moonPhases.json'

const ActivitiesTab = ({ user, userProfile }) => {
  const [currentPhase, setCurrentPhase] = useState(null)
  const [selectedScenario, setSelectedScenario] = useState('business')
  const [trackingMode, setTrackingMode] = useState(null)

  useEffect(() => {
    const mode = userProfile?.tracking_mode || getTrackingMode() || 'cycle'
    setTrackingMode(mode)

    const personalizedPhase = getCurrentPhase()
    
    if (personalizedPhase) {
      let phaseData = null
      if (mode === 'moon') {
        phaseData = moonPhasesData.phases.find(p => 
          p.moonPhase === personalizedPhase.moonPhase
        )
      } else if (mode === 'cycle') {
        phaseData = allPhasesData.phases.find(p => 
          p.menstrualPhase === personalizedPhase.menstrualPhase
        )
      }

      if (phaseData) {
        setCurrentPhase({ ...phaseData, ...personalizedPhase })
      } else {
        setCurrentPhase(allPhasesData.phases[0])
      }
    } else {
      setCurrentPhase(allPhasesData.phases[0])
    }
  }, [userProfile])

  const scenarios = [
    { id: 'business', label: 'Running a business', icon: '💼' },
    { id: 'creation', label: 'In creation mode', icon: '✨' },
    { id: 'kids', label: 'Kids with you today', icon: '👶' },
    { id: 'diary', label: 'Full diary', icon: '📅' },
    { id: 'spiritual', label: 'Spiritual practice', icon: '🕯️' }
  ]

  if (!currentPhase) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-600">Loading your personalized activities...</p>
      </div>
    )
  }

  const currentScenarioActions = currentPhase.scenarios?.[selectedScenario] || []

  const getCycleDayText = () => {
    if (trackingMode === 'cycle' && currentPhase.cycleDay) {
      return `Day ${currentPhase.cycleDay} of your cycle`
    } else if (trackingMode === 'moon' && currentPhase.lunarDay) {
      return `Lunar day ${currentPhase.lunarDay}`
    }
    return null
  }

  return (
    <div className="space-y-6">
      {/* Current Phase Info */}
      <div className="text-center">
        <h2 className="italiana-font text-2xl text-black mb-2">
          You're in <span className="gold-accent">{currentPhase.name}</span>
        </h2>
        
        {getCycleDayText() && (
          <p className="text-sm text-gold font-medium mb-3">
            {getCycleDayText()}
          </p>
        )}
        
        <div className="bg-gray-50 p-4 rounded-lg">
          <p className="text-sm text-gray-700 leading-relaxed">
            {currentPhase.description}
          </p>
        </div>
      </div>

      {/* Scenario Selection */}
      <div>
        <h3 className="italiana-font text-lg text-black mb-4 text-center">
          Choose Your Situation:
        </h3>
        <div className="grid grid-cols-2 gap-2">
          {scenarios.map((scenario) => (
            <motion.button
              key={scenario.id}
              onClick={() => setSelectedScenario(scenario.id)}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`p-3 text-xs rounded-lg border transition-all ${
                selectedScenario === scenario.id
                  ? 'border-gold bg-gold bg-opacity-10 text-black'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400'
              }`}
            >
              <div className="flex flex-col items-center space-y-1">
                <span className="text-lg">{scenario.icon}</span>
                <span className="font-medium leading-tight">{scenario.label}</span>
              </div>
            </motion.button>
          ))}
        </div>
      </div>

      {/* Activities List */}
      <div>
        <h3 className="italiana-font text-lg text-black mb-4 text-center">
          Your Complete Action Plan:
        </h3>
        
        {currentScenarioActions.length > 0 ? (
          <div className="space-y-4">
            {currentScenarioActions.map((action, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="bg-gray-50 p-4 rounded-lg"
              >
                <div className="flex items-start space-x-3">
                  <div className="gold-bg text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-semibold flex-shrink-0 mt-1">
                    {index + 1}
                  </div>
                  <p className="text-sm leading-relaxed text-gray-800">
                    {action}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-600 mb-4">
              No actions available for this scenario in your current phase.
            </p>
            <button
              onClick={() => setSelectedScenario('business')}
              className="text-gold hover:underline"
            >
              Try a different scenario
            </button>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="bg-gray-50 p-4 rounded-lg">
        <h4 className="font-medium text-black mb-3">Quick Actions</h4>
        <div className="space-y-2 text-sm">
          <button className="w-full text-left text-gray-700 hover:text-black transition-colors">
            📝 Add today's activities to calendar
          </button>
          <button className="w-full text-left text-gray-700 hover:text-black transition-colors">
            💡 Get alternative suggestions from coach
          </button>
          <button className="w-full text-left text-gray-700 hover:text-black transition-colors">
            📊 Track how this phase feels for you
          </button>
        </div>
      </div>
    </div>
  )
}

export default ActivitiesTab